<template>
  <tf-button
    v-test:btn="'download coverage'"
    :disabled="isDisabled"
    :loading="isDownloading"
    icon="file-download"
    type="primary"
    size="text"
    @click="onClick"
  >
    <slot>
      Download coverage
    </slot>
  </tf-button>
</template>

<script>
  import { getCoveragesExport } from '@/services/clientCoverages';
  import { downloadRawStream } from '@/utils/file';

  /**
   * "CtaDownloadCoverage" is a component used throughout the site for downloading an xlxs file of a coverage of any type.
   *
   * @vuedoc
   * @exports CtaDownloadCoverage
   * @category Components/CTAs
   */
  export default {
    name: 'CtaDownloadCoverage',
    props: {
      /**
       * Pass a coverage type to be used in analytics and for the API.
       */
      coverageType: {
        type: String,
        default: 'current',
        validator: (v) => ['current', 'upcoming', 'past'].includes(v),
      },
      /**
       * Pass a client ID to be used in analytics and for the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
      /**
       * Pass 'isDisabled' to disable the button in the template.
       */
      isDisabled: {
        type: Boolean,
        default: false,
      },
      /**
       * Pass a product type ID to be used in analytics, but more importantly, if no ID is passed, then an export for all product types will be returned.
       */
      productTypeId: {
        type: [String, Number],
        default: null,
      },
      /**
       * Pass an array of product type IDs to be used in analytics.
       */
      productTypeIds: {
        type: Array,
        default: () => [],
      },
    },
    data: () => ({
      isDownloading: false,
    }),
    methods: {
      /**
       * Download an export for a coverage based on various props.
       */
      async onClick() {
        this.isDownloading = true;

        this.$TF.Analytics.trackEvent('click download coverages', {
          client_id: this.clientId,
          coverage_type: this.coverageType,
          product_type_ids: this.productTypeId ? [this.productTypeId] : this.productTypeIds,
        });

        try {
          const response = await getCoveragesExport(
            {
              clientId: this.clientId,
              coverageType: this.coverageType,
              format: 'xlsx',
              productTypeId: this.productTypeId,
            },
          );

          downloadRawStream(
            response.data,
            '', // empty string for deprecated usage.
            response.headers['content-disposition'],
          );
        } catch (error) {
          this.$message({
            duration: 3000,
            message: `There was an error downloading your ${this.coverageType} coverage.`,
            showClose: true,
            type: 'error',
          });
        } finally {
          this.isDownloading = false;
        }
      },
    },
  };
</script>
